import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { LineItem } from 'shared/types/cart/LineItem';
import { Variant } from 'shared/types/product';
import { Category } from 'shared/types/product/Category';
import Breadcrumb from 'components/commercetools-ui/atoms/breadcrumb';
import Button from 'components/commercetools-ui/atoms/button';
import Link from 'components/commercetools-ui/atoms/link';
import Typography from 'components/commercetools-ui/atoms/typography';
import Gallery from 'components/commercetools-ui/organisms/gallery';
import ProductInformation from 'components/commercetools-ui/organisms/product/product-details/components/product-information';
import useTrack from 'components/commercetools-ui/organisms/product/product-details/hooks/useTrack';
import { UIProduct } from 'components/commercetools-ui/organisms/product/product-details/types';
import Alert from 'components/padi-ui/alert';
import { useAddToCartOverlay } from 'context/add-to-cart-overlay';
import { PDP_PRODUCT_ADDED_TO_CART } from 'helpers/constants/events';
import useClassNames from 'helpers/hooks/useClassNames';
import { useFormat } from 'helpers/hooks/useFormat';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { desktop } from 'helpers/utils/screensizes';
import { getLocalStorage } from 'helpers/utils/localStorage';
import { useCart, useProduct } from 'frontastic';
import Upsell from '../upsell/subscription';

export interface ProductDetailsSubscriptionProps {
  product: UIProduct;
  variant: Variant;
  url?: string;
  onChangeVariant: (sku: string) => void;
  inModalVersion?: boolean;
  setIsOpen?: (value: boolean) => void;
  onAddToCart?: () => void;
}

const ProductDetailsSubscription: FC<ProductDetailsSubscriptionProps> = ({
  product,
  variant,
  url,
  onChangeVariant,
  inModalVersion,
  setIsOpen,
  onAddToCart,
}) => {
  const { addItem, data } = useCart();
  const { categories } = useProduct();
  const [isDesktopSize] = useMediaQuery(desktop);

  const [loading, setLoading] = useState(true);
  const [added, setAdded] = useState(false);
  const [category, setCategory] = useState<Category>();
  const [isClubInCart, setIsClubInCart] = useState<boolean | undefined>(undefined);

  const [isUserInUS, setIsUserInUS] = useState(false);
  const [isLoadingCountryCheck, setIsLoadingCountryCheck] = useState(true);

  const { formatMessage: formatMessageCart } = useFormat({ name: 'cart' });
  const { formatMessage: formatMessagePadi } = useFormat({ name: 'padi' });

  const checkIfClubInCart = useCallback(async (lineItems: LineItem[]) => {
    if (!lineItems) return false;
    let clubInCart = false;
    lineItems?.some((lineItem) => {
      if (lineItem?.productKey == 'padi-club-membership') clubInCart = true;
    });
    setIsClubInCart(clubInCart);
  }, []);

  useEffect(() => {
    if (isClubInCart === undefined) checkIfClubInCart(data?.lineItems as LineItem[]);
    if (product && categories && !category) {
      const currentCategory = categories.find((cat) => cat.categoryId === product.categories?.[0]?.categoryId);
      setCategory(currentCategory);
    }
    setLoading(false);
  }, [categories, category, checkIfClubInCart, data?.lineItems, isClubInCart, product]);

  useEffect(() => {
    const userCountry = getLocalStorage('padiUserCountry');
    if (userCountry?.toLowerCase() === 'us') {
      setIsUserInUS(true);
    }
    setIsLoadingCountryCheck(false);
  }, []);

  const { trackAddToCart } = useTrack({ product, inModalVersion });

  const { show, fetchRelatedProducts } = useAddToCartOverlay();

  const handleAddToCart = async () => {
    setLoading(true);

    const quantity = 1;
    addItem(product, variant, quantity).then(() => {
      setLoading(false);

      setAdded(true);
      setTimeout(() => {
        setAdded(false);
        setIsClubInCart(true);
      }, 1000);

      show(product, variant, quantity);
      onAddToCart?.();
    });
    trackAddToCart(quantity);
  };

  useEffect(() => {
    fetchRelatedProducts(product);
  }, [product, fetchRelatedProducts]);

  const showMoreInfo = useMemo(() => {
    return product?.variants[0].attributes?.['section-17'];
  }, [product]);

  const wrapperClassName = inModalVersion
    ? 'md:grid grid-cols-12 pt-70 pb-35 px-20 md:pr-36'
    : 'pt-20 md:pt-24 pb-32 md:grid md:grid-cols-12 md:items-start mx-auto max-w-7xl px-24 lg:px-32';

  const galleryContainerClassName = useClassNames([
    inModalVersion ? 'col-span-6' : 'md:col-span-7 lg:col-span-8',
    'ltr:md:pr-26 ltr:lg:pr-60 h-auto min-h-200 rtl:md:pl-26 rtl:lg:pl-60',
  ]);

  const informationContainerClassName = useClassNames([
    inModalVersion ? 'col-span-6' : 'md:col-span-5 lg:col-span-4',
    'mt-24 md:mt-0 h-auto min-h-150',
  ]);

  return (
    <div className={wrapperClassName}>
      {category && isDesktopSize && !inModalVersion && (
        <Breadcrumb Separator="/" className="col-span-12 mb-24 w-fit">
          <Link key={category.categoryId} link={category._url} className="text-14 text-primary-black">
            {category.name}
          </Link>

          <Typography key={product.slug} className="cursor-default text-14 text-neutral-500">
            {product.name}
          </Typography>
        </Breadcrumb>
      )}

      <div className={galleryContainerClassName}>
        <Gallery
          images={variant?.images ?? []}
          imagesExtra={variant?.imagesExtra ?? []}
          inModalVersion={inModalVersion}
          firstImagePriority={true}
          hideArrowsOnSingleSlide={true}
        />
      </div>

      <div className={informationContainerClassName}>
        <ProductInformation
          product={product}
          variant={variant}
          onChangeVariant={onChangeVariant}
          inModalVersion={inModalVersion}
          priceLabel={` / ${formatMessageCart({ id: 'subscription.club.term.label', defaultMessage: 'year' })}`}
        />

        <div className="flex flex-col gap-8 pt-20">
          <Button
            className="w-full rounded-sm text-14 font-medium"
            variant="primary"
            onClick={handleAddToCart}
            loading={loading}
            added={added}
            dataAnalytics={PDP_PRODUCT_ADDED_TO_CART}
            disabled={isClubInCart}
          >
            {formatMessageCart({ id: 'cart.add', defaultMessage: 'Add to cart' })}
          </Button>

          {!loading && isClubInCart && (
            <Alert
              alertType="warning"
              body={`${formatMessagePadi({
                id: 'club.pdp.quantity.alert',
                defaultMessage: `We're sorry, but you cannot add more than one PADI Club Membership to your cart.`,
              })}`}
            />
          )}
        </div>

        {showMoreInfo && !isLoadingCountryCheck && isUserInUS && <Upsell product={product} />}

        {inModalVersion && (
          <Link
            link={url}
            className="mx-auto mt-28 block w-fit border-b border-transparent text-center text-14 leading-loose text-secondary-black hover:border-secondary-black"
            onClick={() => setIsOpen?.(false)}
          >
            {formatMessageCart({ id: 'more.details', defaultMessage: 'More details' })}
          </Link>
        )}
      </div>
    </div>
  );
};

export default ProductDetailsSubscription;
